import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssignmentRuleUpdate, TransactionAssignInput, TransactionNoteInput } from '@app/shared';
import {
  assignTransaction,
  assignTransactionsBulk,
  deleteAssignmentRule,
  fetchAssignmentQueue,
  fetchAssignmentRules,
  replayAssignmentRule,
  updateAssignmentRule,
  updateTransactionNote,
} from './api';

const queueKey = ['assignment-queue'] as const;
const rulesKey = ['assignment-rules'] as const;

export const assignmentQueueQueryOptions = queryOptions({ queryKey: queueKey, queryFn: fetchAssignmentQueue });
export const assignmentRulesQueryOptions = queryOptions({ queryKey: rulesKey, queryFn: fetchAssignmentRules });

export function useAssignmentQueue() {
  return useQuery(assignmentQueueQueryOptions);
}

export function useAssignmentRules() {
  return useQuery(assignmentRulesQueryOptions);
}

function invalidateAfterAssignment(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queueKey });
  queryClient.invalidateQueries({ queryKey: rulesKey });
  queryClient.invalidateQueries({ queryKey: ['transactions'] });
  queryClient.invalidateQueries({ queryKey: ['annual-summary'] });
}

export function useAssignTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: TransactionAssignInput }) => assignTransaction(id, input),
    onSuccess: () => invalidateAfterAssignment(queryClient),
  });
}

export function useAssignTransactionsBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionIds, input }: { transactionIds: number[]; input: TransactionAssignInput }) =>
      assignTransactionsBulk(transactionIds, input),
    onSuccess: () => invalidateAfterAssignment(queryClient),
  });
}

export function useUpdateTransactionNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: TransactionNoteInput }) => updateTransactionNote(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['transactions'] }),
  });
}

export function useUpdateAssignmentRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: AssignmentRuleUpdate }) => updateAssignmentRule(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rulesKey }),
  });
}

export function useDeleteAssignmentRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteAssignmentRule(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rulesKey }),
  });
}

export function useReplayAssignmentRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => replayAssignmentRule(id),
    onSuccess: () => invalidateAfterAssignment(queryClient),
  });
}
